import type { ZodType } from 'zod';
import { getDB } from './db';
import { defaultMeta, defaultSettings } from './defaults';
import {
  AssetSchema,
  DeliverySchema,
  DesignSchema,
  MetaSchema,
  NoteSchema,
  ScheduleSchema,
  SettingsSchema,
  SnapshotSchema,
  type Asset,
  type Delivery,
  type Design,
  type Meta,
  type Note,
  type Schedule,
  type Settings,
  type Snapshot,
} from './schemas';

export interface QuarantinedRecord {
  store: string;
  key: string;
  issues: string;
  raw: unknown;
}

function validate<T>(schema: ZodType<T>, raw: unknown, store: string, quarantine: QuarantinedRecord[]): T | null {
  const r = schema.safeParse(raw);
  if (r.success) return r.data;
  const key = (raw as { id?: string; key?: string } | null)?.id ?? (raw as { key?: string } | null)?.key ?? '?';
  quarantine.push({ store, key: String(key), issues: r.error.issues.slice(0, 3).map((i) => `${i.path.join('.')}: ${i.message}`).join('; '), raw });
  return null;
}

export interface LoadedData {
  notes: Note[];
  designs: Design[];
  schedules: Schedule[];
  settings: Settings;
  meta: Meta;
  quarantine: QuarantinedRecord[];
}

export async function loadAll(): Promise<LoadedData> {
  const db = await getDB();
  const quarantine: QuarantinedRecord[] = [];
  const [rawNotes, rawDesigns, rawSchedules, rawSettings, rawMeta] = await Promise.all([
    db.getAll('notes'),
    db.getAll('designs'),
    db.getAll('schedules'),
    db.get('meta', 'settings'),
    db.get('meta', 'meta'),
  ]);
  const notes = rawNotes.map((n) => validate(NoteSchema, n, 'notes', quarantine)).filter((n): n is Note => !!n);
  const designs = rawDesigns.map((d) => validate(DesignSchema, d, 'designs', quarantine)).filter((d): d is Design => !!d);
  const schedules = rawSchedules.map((s) => validate(ScheduleSchema, s, 'schedules', quarantine)).filter((s): s is Schedule => !!s);
  // Settings are merged over defaults so new fields added in later versions get sensible values.
  const settingsCandidate = { ...defaultSettings(), ...(rawSettings && typeof rawSettings === 'object' ? rawSettings : {}) };
  const settings = SettingsSchema.safeParse(settingsCandidate).success ? SettingsSchema.parse(settingsCandidate) : defaultSettings();
  const metaCandidate = { ...defaultMeta(), ...(rawMeta && typeof rawMeta === 'object' ? rawMeta : {}) };
  const meta = MetaSchema.safeParse(metaCandidate).success ? MetaSchema.parse(metaCandidate) : defaultMeta();
  if (!rawMeta) await db.put('meta', meta, 'meta');
  return { notes, designs, schedules, settings, meta, quarantine };
}

// ---------------------------------------------------------------- notes

export type SaveResult = { ok: true; note: Note } | { ok: false; conflict: Note };

/**
 * Optimistic-concurrency save. `note.rev` is the revision this copy was based on.
 * Succeeds when the stored revision matches (or the note is new) and writes rev + 1.
 */
export async function saveNote(note: Note): Promise<SaveResult> {
  const db = await getDB();
  const tx = db.transaction('notes', 'readwrite');
  const stored = await tx.store.get(note.id);
  if (stored && stored.rev > note.rev) {
    await tx.done;
    return { ok: false, conflict: stored };
  }
  const next: Note = NoteSchema.parse({ ...note, rev: (stored?.rev ?? note.rev) + 1 });
  await tx.store.put(next);
  await tx.done;
  return { ok: true, note: next };
}

/** Write without the revision check (imports, restores after conflict resolution). */
export async function putNote(note: Note): Promise<Note> {
  const db = await getDB();
  const parsed = NoteSchema.parse(note);
  await db.put('notes', parsed);
  return parsed;
}

export async function getNote(id: string): Promise<Note | undefined> {
  const db = await getDB();
  const raw = await db.get('notes', id);
  const r = NoteSchema.safeParse(raw);
  return r.success ? r.data : undefined;
}

/** Permanently remove a note with its snapshots and schedules. */
export async function purgeNote(id: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['notes', 'snapshots', 'schedules'], 'readwrite');
  await tx.objectStore('notes').delete(id);
  for (const k of await tx.objectStore('snapshots').index('noteId').getAllKeys(id)) await tx.objectStore('snapshots').delete(k);
  for (const k of await tx.objectStore('schedules').index('noteId').getAllKeys(id)) await tx.objectStore('schedules').delete(k);
  await tx.done;
}

// ---------------------------------------------------------------- assets

export async function putAsset(asset: Asset): Promise<Asset> {
  const db = await getDB();
  const existing = await db.getFromIndex('assets', 'hash', asset.hash);
  if (existing && existing.kind === asset.kind) return existing;
  await db.put('assets', AssetSchema.parse(asset));
  return asset;
}

export async function getAsset(id: string): Promise<Asset | undefined> {
  const db = await getDB();
  return db.get('assets', id);
}

export async function listAssetsMeta(kind?: Asset['kind']): Promise<Omit<Asset, 'data'>[]> {
  const db = await getDB();
  const all = await db.getAll('assets');
  return all
    .filter((a) => !kind || a.kind === kind)
    .map(({ data: _data, ...rest }) => rest)
    .sort((a, b) => b.createdAt - a.createdAt);
}

export async function deleteAsset(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('assets', id);
}

/** Delete image assets no note, snapshot or setting references. Returns the number removed. */
export async function collectGarbageAssets(keep: Set<string>): Promise<number> {
  const db = await getDB();
  const [notes, snaps, assets] = await Promise.all([db.getAll('notes'), db.getAll('snapshots'), db.getAllKeys('assets')]);
  for (const n of notes) for (const i of n.images ?? []) keep.add(i.assetId);
  for (const s of snaps) for (const i of s.note?.images ?? []) keep.add(i.assetId);
  let removed = 0;
  for (const id of assets) {
    if (!keep.has(id)) {
      await db.delete('assets', id);
      removed++;
    }
  }
  return removed;
}

// ---------------------------------------------------------------- designs

export async function putDesign(d: Design): Promise<void> {
  const db = await getDB();
  await db.put('designs', DesignSchema.parse(d));
}

export async function deleteDesign(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('designs', id);
}

// ---------------------------------------------------------------- schedules & deliveries

export async function putSchedule(s: Schedule): Promise<void> {
  const db = await getDB();
  await db.put('schedules', ScheduleSchema.parse(s));
}

export async function deleteSchedule(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('schedules', id);
}

/**
 * Atomically claim an occurrence for delivery. Returns false when it was already delivered
 * (by this tab, another tab, or a previous session) — the core of duplicate prevention.
 */
export async function claimDelivery(d: Delivery): Promise<boolean> {
  const db = await getDB();
  const tx = db.transaction('deliveries', 'readwrite');
  const existing = await tx.store.get(d.key);
  if (existing) {
    await tx.done;
    return false;
  }
  await tx.store.add(DeliverySchema.parse(d));
  await tx.done;
  return true;
}

export async function updateDelivery(key: string, patch: Partial<Delivery>): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('deliveries', 'readwrite');
  const existing = await tx.store.get(key);
  if (existing) await tx.store.put({ ...existing, ...patch });
  await tx.done;
}

export async function listDeliveries(scheduleId?: string, limit = 200): Promise<Delivery[]> {
  const db = await getDB();
  const all = scheduleId ? await db.getAllFromIndex('deliveries', 'scheduleId', scheduleId) : await db.getAll('deliveries');
  return all.sort((a, b) => b.firedAt - a.firedAt).slice(0, limit);
}

export async function pruneDeliveries(olderThan: number): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('deliveries', 'readwrite');
  let cursor = await tx.store.index('firedAt').openCursor(IDBKeyRange.upperBound(olderThan));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

// ---------------------------------------------------------------- snapshots

export async function addSnapshot(s: Snapshot): Promise<void> {
  const db = await getDB();
  await db.put('snapshots', SnapshotSchema.parse(s));
}

export async function listSnapshots(noteId?: string): Promise<Snapshot[]> {
  const db = await getDB();
  const raw = noteId ? await db.getAllFromIndex('snapshots', 'noteId', noteId) : await db.getAll('snapshots');
  const out: Snapshot[] = [];
  for (const r of raw) {
    const p = SnapshotSchema.safeParse(r);
    if (p.success) out.push(p.data);
  }
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

export async function deleteSnapshot(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('snapshots', id);
}

/** Apply retention: keep at most `maxPerNote` per note and nothing older than `maxAgeDays` (manual ones are kept by age only). */
export async function pruneSnapshots(maxPerNote: number, maxAgeDays: number, now = Date.now()): Promise<number> {
  const db = await getDB();
  const all = await db.getAll('snapshots');
  const byNote = new Map<string, Snapshot[]>();
  for (const s of all) byNote.set(s.noteId, [...(byNote.get(s.noteId) ?? []), s]);
  const cutoff = now - maxAgeDays * 864e5;
  const doomed: string[] = [];
  for (const list of byNote.values()) {
    list.sort((a, b) => b.createdAt - a.createdAt);
    list.forEach((s, i) => {
      if (s.createdAt < cutoff || (i >= maxPerNote && s.reason !== 'manual')) doomed.push(s.id);
    });
  }
  const tx = db.transaction('snapshots', 'readwrite');
  for (const id of doomed) await tx.store.delete(id);
  await tx.done;
  return doomed.length;
}

// ---------------------------------------------------------------- settings & meta

export async function saveSettings(s: Settings): Promise<void> {
  const db = await getDB();
  await db.put('meta', SettingsSchema.parse(s), 'settings');
}

export async function saveMeta(m: Meta): Promise<void> {
  const db = await getDB();
  await db.put('meta', MetaSchema.parse(m), 'meta');
}

// ---------------------------------------------------------------- everything

export async function clearAllData(): Promise<void> {
  const db = await getDB();
  const stores = ['notes', 'assets', 'designs', 'snapshots', 'schedules', 'deliveries', 'meta'] as const;
  const tx = db.transaction([...stores], 'readwrite');
  await Promise.all(stores.map((s) => tx.objectStore(s).clear()));
  await tx.done;
}

export async function storageEstimate(): Promise<{ usage: number; quota: number; persisted: boolean } | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  const [est, persisted] = await Promise.all([navigator.storage.estimate(), navigator.storage.persisted?.() ?? Promise.resolve(false)]);
  return { usage: est.usage ?? 0, quota: est.quota ?? 0, persisted };
}

export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
